import { getMediaStream, clearMediaStream } from "./media-stream-api";
import { AudioStreamFormat } from "../../../models/microphone";
import { DEFAULT_AUDIO_STREAM } from "../../../constants/microphone";
import {
  getAudioContext,
  getScriptProcessorNode,
  getMediaStreamSource,
  clearAudioContext,
  clearMediaStreamSource,
  clearScriptProcessorNode
} from "./web-audio-api";

interface ScriptNodeManagerOptions {
  audioCtx?: AudioContext;
  log?: (msg: string) => void;
}

/**
 * @todo use AudioWorklet: https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/PCMRecorder.ts#L54
 */
export class ScriptNodeManager {
  // --------------------------------------------------------------------------
  //    Attributes
  // --------------------------------------------------------------------------
  private _audioContext?: AudioContext;
  private _mediaStreamSource?: MediaStreamAudioSourceNode;
  private _scriptProcessorNode?: ScriptProcessorNode;
  private _mediaStream?: MediaStream;
  /**
   * If an AudioContext has been initialised, save the sampleRate so that the
   * value is still available even if the AudioContext has been cleared
   */
  private _audioContextSampleRate = 0;

  private _log: (msg: string) => void;

  // --------------------------------------------------------------------------
  //    Initialisation
  // --------------------------------------------------------------------------
  constructor(options: ScriptNodeManagerOptions = {}) {
    this._log = options.log || ((_: string) => {});
  }

  // --------------------------------------------------------------------------
  //    Getters
  // --------------------------------------------------------------------------
  get audioContext(): AudioContext | undefined {
    return this._audioContext;
  }

  get micInput(): MediaStreamAudioSourceNode | undefined {
    return this._mediaStreamSource;
  }

  get scriptProcessorNode(): ScriptProcessorNode | undefined {
    return this._scriptProcessorNode;
  }

  get mediaStream(): MediaStream | undefined {
    return this._mediaStream;
  }

  get audioContextSampleRate(): number {
    return this._audioContextSampleRate;
  }

  // --------------------------------------------------------------------------
  //    Methods
  // --------------------------------------------------------------------------

  async start(
    onNewData: (ev: AudioProcessingEvent) => void,
    micStreamFormat: AudioStreamFormat = DEFAULT_AUDIO_STREAM
  ) {
    try {
      // AudioContext
      const ctx = getAudioContext();
      // MediaStream
      const stream = await getMediaStream();
      // ScriptProcessorNode
      const scriptNode = getScriptProcessorNode(
        ctx,
        onNewData,
        micStreamFormat
      );
      // StreamSource
      const micInput = getMediaStreamSource(ctx, stream);

      // Ensure that AudioContext has a running state
      // https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/MicAudioSource.ts#L150
      if (ctx.state === "suspended") {
        this.log("start(): AudioContext is resumed");
        await ctx.resume();
      }

      this._audioContext = ctx;
      this._mediaStream = stream;
      this._scriptProcessorNode = scriptNode;
      this._mediaStreamSource = micInput;
      this.log(`start() with _audioContext: ${this._audioContext} `);
      this.log(`start() with _mediaStream: ${this._mediaStream} `);
      this.log(`start() with _scriptNode: ${this._scriptProcessorNode} `);
      this.log(`start() with _mediaStreamSource: ${this._mediaStreamSource} `);

      this._audioContextSampleRate = ctx.sampleRate;
    } catch (err) {
      // If any error is happening during one of the resource initialisation, it
      // is up to the caller to handle the error. This method acts as a
      // passthrough
      throw err;
    }

    // Connect!
    this._mediaStreamSource.connect(this._scriptProcessorNode);
    this._scriptProcessorNode.connect(this._audioContext.destination);
  }

  async stop() {
    this._mediaStreamSource = clearMediaStreamSource(this._mediaStreamSource);
    this._scriptProcessorNode = clearScriptProcessorNode(
      this._audioContext,
      this._scriptProcessorNode
    );
    this._mediaStream = clearMediaStream(this._mediaStream);
    this._audioContext = clearAudioContext(this._audioContext);

    this.log(`stop() with _audioContext: ${this._audioContext} `);
    this.log(`stop() with _mediaStream: ${this._mediaStream} `);
    this.log(`stop() with _scriptNode: ${this._scriptProcessorNode} `);
    this.log(`stop() with _mediaStreamSource: ${this._mediaStreamSource} `);
  }

  log(msg: string) {
    this._log(msg);
  }
}
